import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import type { DepositSpec, RockSpec } from '../content/types';
import { createModelLoadingManager } from './assetLoading';

type Random = () => number;
type DepositType = DepositSpec['type'];

const NATURE_ROOT = '/assets/environment/kenney-nature';
const CASTLE_ROOT = '/assets/environment/kenney-castle';
const CRYSTAL_MODEL = '/assets/environment/aredon-crystals/crystal-cluster.obj';

const gltfLoader = new GLTFLoader(createModelLoadingManager());
const objLoader = new OBJLoader();
const gltfSources = new Map<string, Promise<THREE.Object3D>>();
const objSources = new Map<string, Promise<THREE.Object3D>>();

const TALL_ROCKS = [
  'rock_tallA.glb',
  'rock_tallB.glb',
  'rock_tallC.glb',
  'rock_tallG.glb',
  'rock_tallH.glb',
  'rock_tallJ.glb',
] as const;
const LARGE_ROCKS = [
  'rock_largeA.glb',
  'rock_largeB.glb',
  'rock_largeC.glb',
  'rock_largeD.glb',
  'rock_largeE.glb',
  'rock_largeF.glb',
] as const;

const RIDGE_PALETTES = [
  [0x5b6461, 0x818b7c, 0x414947], // weathered granite + lichen
  [0x4b5962, 0x73838a, 0x343f45], // blue slate
  [0x6c6254, 0x958369, 0x4b443b], // warm limestone
  [0x565c60, 0x7d8585, 0x3b4246], // cool boulder field
] as const;

export type FallbackLoadResult = { ok: true } | { ok: false; error: unknown };

const reportedAssetFailures = new Set<string>();

function reportAssetFailure(key: string, message: string, error: unknown): void {
  if (reportedAssetFailures.has(key)) return;
  reportedAssetFailures.add(key);
  console.error(message, error);
}

function disposeObject(object: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    geometries.add(child.geometry);
    const owned = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of owned) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}

/** Atomically swaps a visible fallback only after its imported replacement is ready. */
export async function replaceFallbackWhenLoaded(
  parent: THREE.Group,
  fallback: THREE.Object3D,
  load: () => Promise<THREE.Object3D>,
): Promise<FallbackLoadResult> {
  try {
    const imported = await load();
    parent.add(imported);
    parent.remove(fallback);
    disposeObject(fallback);
    return { ok: true };
  } catch (error: unknown) {
    return { ok: false, error };
  }
}

function terrainRandom(seed: number): Random {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function stoneMaterial(color: number, emissiveLift = 0.08): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.96,
    metalness: 0,
    flatShading: true,
    emissive: new THREE.Color(color).multiplyScalar(emissiveLift),
    emissiveIntensity: 0.45,
  });
}

function groundedDodecahedron(): THREE.DodecahedronGeometry {
  const geometry = new THREE.DodecahedronGeometry(1, 0);
  geometry.computeBoundingBox();
  geometry.translate(0, -(geometry.boundingBox?.min.y ?? -1), 0);
  return geometry;
}

/** Immediate collision-readable terrain used until the downloaded rock kit is ready. */
export function makeProceduralRockFallback(spec: RockSpec, formationIndex: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'rock-fallback';
  const rnd = terrainRandom(mountainSeed(spec, formationIndex) ^ 0x27d4eb2f);
  const palette = RIDGE_PALETTES[formationIndex % RIDGE_PALETTES.length];
  const materials = palette.map((color) => stoneMaterial(color, 0.05));
  const longAlongZ = spec.h >= spec.w;
  const longSize = Math.max(spec.w, spec.h);
  const shortSize = Math.min(spec.w, spec.h);
  const isLongRidge = longSize / shortSize > 2.2;
  const count = isLongRidge ? 6 : 3;

  for (let index = 0; index < count; index++) {
    const rock = new THREE.Mesh(groundedDodecahedron(), materials[index % materials.length]);
    const along = isLongRidge ? ((index + 0.5) / count - 0.5) * longSize * 0.82 : (index - 1) * spec.w * 0.16;
    const cross = (rnd() - 0.5) * shortSize * 0.22;
    const width = Math.max(1.2, shortSize * (isLongRidge ? 0.23 : 0.28) * (0.85 + rnd() * 0.3));
    const depth = Math.max(1.2, shortSize * (isLongRidge ? 0.28 : 0.32) * (0.85 + rnd() * 0.3));
    const height = (isLongRidge ? 2.4 : 2.1) + rnd() * (isLongRidge ? 2.5 : 1.8);
    rock.position.set(longAlongZ ? cross : along, 0, longAlongZ ? along : cross);
    rock.rotation.y = rnd() * Math.PI;
    rock.scale.set(width, height, depth);
    rock.castShadow = true;
    rock.receiveShadow = true;
    group.add(rock);
  }

  return group;
}

function sourceGLB(path: string): Promise<THREE.Object3D> {
  let pending = gltfSources.get(path);
  if (!pending) {
    pending = gltfLoader.loadAsync(path).then((gltf) => gltf.scene);
    gltfSources.set(path, pending);
  }
  return pending;
}

function sourceOBJ(path: string): Promise<THREE.Object3D> {
  let pending = objSources.get(path);
  if (!pending) {
    pending = objLoader.loadAsync(path);
    objSources.set(path, pending);
  }
  return pending;
}

function styleMeshes(
  object: THREE.Object3D,
  materialFor: (original: THREE.Material, slot: number, mesh: number) => THREE.Material,
): void {
  let meshIndex = 0;
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.castShadow = true;
    child.receiveShadow = true;
    child.material = Array.isArray(child.material)
      ? child.material.map((material, slot) => materialFor(material, slot, meshIndex))
      : materialFor(child.material, 0, meshIndex);
    meshIndex++;
  });
}

/** Centers a downloaded model on its pivot, grounds it, and bounds it without distorting it. */
function fitModel(object: THREE.Object3D, height: number, maxWidth: number, maxDepth: number): void {
  object.updateMatrixWorld(true);
  const initial = new THREE.Box3().setFromObject(object);
  const size = initial.getSize(new THREE.Vector3());
  const scale = height / Math.max(0.001, size.y);
  object.scale.setScalar(scale);
  object.updateMatrixWorld(true);

  const scaled = new THREE.Box3().setFromObject(object);
  const scaledSize = scaled.getSize(new THREE.Vector3());
  if (scaledSize.x > maxWidth) object.scale.x *= maxWidth / scaledSize.x;
  if (scaledSize.z > maxDepth) object.scale.z *= maxDepth / scaledSize.z;
  object.updateMatrixWorld(true);

  const fitted = new THREE.Box3().setFromObject(object);
  const center = fitted.getCenter(new THREE.Vector3());
  object.position.x -= center.x;
  object.position.z -= center.z;
  object.position.y -= fitted.min.y;
}

async function preparedGLB(
  path: string,
  height: number,
  maxWidth: number,
  maxDepth: number,
  materials: readonly THREE.Material[],
): Promise<THREE.Group> {
  const object = (await sourceGLB(path)).clone(true);
  styleMeshes(object, (_original, slot, mesh) => materials[(slot + mesh) % materials.length]);
  fitModel(object, height, maxWidth, maxDepth);
  const wrapper = new THREE.Group();
  wrapper.add(object);
  return wrapper;
}

function mountainSeed(spec: RockSpec, formationIndex: number): number {
  return (
    0x51f15e5d ^
    Math.imul(formationIndex + 1, 0x9e3779b1) ^
    Math.imul(Math.round(spec.pos.x * 10), 0x85ebca6b) ^
    Math.imul(Math.round(spec.pos.y * 10), 0xc2b2ae35)
  );
}

async function populateLongRidge(group: THREE.Group, spec: RockSpec, formationIndex: number): Promise<void> {
  const rnd = terrainRandom(mountainSeed(spec, formationIndex));
  const palette = RIDGE_PALETTES[formationIndex % RIDGE_PALETTES.length];
  const materials = palette.map((color, index) => stoneMaterial(color, index === 1 ? 0.11 : 0.06));
  const longAlongZ = spec.h >= spec.w;
  const longSize = Math.max(spec.w, spec.h);
  const shortSize = Math.min(spec.w, spec.h);
  const clusterT = [-0.66, -0.23, 0.22, 0.65];
  const heightProfiles = [
    [4.5, 6.2, 5.4, 4.8],
    [6.4, 4.7, 6.8, 5.3],
    [4.3, 5.6, 6.1, 4.5],
    [5.1, 6.1, 4.6, 5.8],
  ][formationIndex % 4];
  const additions: Promise<void>[] = [];

  for (let i = 0; i < clusterT.length; i++) {
    const along = clusterT[i] * (longSize / 2);
    const cross = ((i + formationIndex) % 2 === 0 ? -1 : 1) * shortSize * (0.08 + rnd() * 0.07);
    const primaryName = TALL_ROCKS[(formationIndex * 2 + i) % TALL_ROCKS.length];
    const secondaryName = LARGE_ROCKS[(formationIndex + i * 2) % LARGE_ROCKS.length];
    const primaryHeight = heightProfiles[i] * (0.94 + rnd() * 0.12);

    additions.push((async () => {
      const primary = await preparedGLB(
        `${NATURE_ROOT}/${primaryName}`,
        primaryHeight,
        shortSize * 0.72,
        Math.min(5.2, longSize * 0.18),
        materials,
      );
      primary.position.set(longAlongZ ? cross : along, 0, longAlongZ ? along : cross);
      primary.rotation.y = (rnd() - 0.5) * 0.55;
      group.add(primary);

      const secondary = await preparedGLB(
        `${NATURE_ROOT}/${secondaryName}`,
        1.8 + rnd() * 1.15,
        shortSize * 0.48,
        3.4,
        [materials[2], materials[0], materials[1]],
      );
      const side = i % 2 === 0 ? 1 : -1;
      const secondaryCross = cross + side * shortSize * (0.18 + rnd() * 0.05);
      const secondaryAlong = along + (rnd() - 0.5) * 2.4;
      secondary.position.set(
        longAlongZ ? secondaryCross : secondaryAlong,
        0,
        longAlongZ ? secondaryAlong : secondaryCross,
      );
      secondary.rotation.y = rnd() * Math.PI;
      group.add(secondary);
    })());
  }

  // Low connector outcrops make each formation a natural wall rather than a row of pillars.
  for (let i = 0; i < clusterT.length - 1; i++) {
    additions.push((async () => {
      const along = ((clusterT[i] + clusterT[i + 1]) / 2) * (longSize / 2);
      const connector = await preparedGLB(
        `${NATURE_ROOT}/${LARGE_ROCKS[(formationIndex * 3 + i) % LARGE_ROCKS.length]}`,
        1.55 + rnd() * 0.5,
        shortSize * 0.58,
        3.8,
        [materials[0], materials[2]],
      );
      const cross = (rnd() - 0.5) * shortSize * 0.2;
      connector.position.set(longAlongZ ? cross : along, 0, longAlongZ ? along : cross);
      connector.rotation.y = rnd() * Math.PI;
      group.add(connector);
    })());
  }
  await Promise.all(additions);
}

async function populateCompactOutcrop(group: THREE.Group, spec: RockSpec, formationIndex: number): Promise<void> {
  const rnd = terrainRandom(mountainSeed(spec, formationIndex));
  const palette = RIDGE_PALETTES[formationIndex % RIDGE_PALETTES.length];
  const materials = palette.map((color) => stoneMaterial(color));
  const additions: Promise<void>[] = [];
  const placements = [
    { x: -spec.w * 0.16, z: 0, h: 3.8 },
    { x: spec.w * 0.12, z: spec.h * 0.12, h: 2.7 },
    { x: spec.w * 0.18, z: -spec.h * 0.18, h: 1.8 },
  ];
  for (let i = 0; i < placements.length; i++) {
    additions.push((async () => {
      const placement = placements[i];
      const file = i === 0
        ? TALL_ROCKS[(formationIndex + i) % TALL_ROCKS.length]
        : LARGE_ROCKS[(formationIndex * 2 + i) % LARGE_ROCKS.length];
      const model = await preparedGLB(
        `${NATURE_ROOT}/${file}`,
        placement.h * (0.9 + rnd() * 0.2),
        spec.w * 0.52,
        spec.h * 0.58,
        materials,
      );
      model.position.set(placement.x, 0, placement.z);
      model.rotation.y = rnd() * Math.PI;
      group.add(model);
    })());
  }
  await Promise.all(additions);
}

/** A downloaded-model mountain wall; navigation continues to use the RockSpec ellipse. */
export function makeRockFormation(spec: RockSpec, formationIndex: number): THREE.Group {
  const group = new THREE.Group();
  group.position.set(spec.pos.x, 0, spec.pos.y);
  const fallback = makeProceduralRockFallback(spec, formationIndex);
  group.add(fallback);
  const isLongRidge = Math.max(spec.w, spec.h) / Math.min(spec.w, spec.h) > 2.2;
  void replaceFallbackWhenLoaded(group, fallback, async () => {
    const imported = new THREE.Group();
    imported.name = 'rock-imported';
    if (isLongRidge) await populateLongRidge(imported, spec, formationIndex);
    else await populateCompactOutcrop(imported, spec, formationIndex);
    return imported;
  }).then((result) => {
    if (result.ok) return;
    reportAssetFailure(
      'mountain-models',
      `Unable to load mountain models from ${NATURE_ROOT}; procedural rock fallbacks retained.`,
      result.error,
    );
  });
  return group;
}

/** Populates one claimable wall foundation with imported masonry and rubble piles. */
export function populateRubbleModel(group: THREE.Group, seed: number): void {
  const rnd = terrainRandom(seed ^ 0x34a9b27d);
  const stone = stoneMaterial(0x6f706b, 0.08);
  const dark = stoneMaterial(0x50534f, 0.05);
  const edge = stoneMaterial(0x88857b, 0.09);
  const materials = [stone, dark, edge];
  const pieces = [
    { file: 'rocks-large.glb', z: -4, x: -0.15, height: 1.15, roll: 0 },
    { file: 'wall-stud.glb', z: -2.1, x: 0.25, height: 1.2, roll: Math.PI / 2.2 },
    { file: 'rocks-small.glb', z: -0.35, x: -0.3, height: 0.8, roll: 0 },
    { file: 'wall-half-modular.glb', z: 1.65, x: 0.18, height: 1.35, roll: -Math.PI / 2.5 },
    { file: 'rocks-large.glb', z: 4, x: 0.1, height: 1.05, roll: 0 },
  ];
  void Promise.all(pieces.map(async (piece, index) => {
    const model = await preparedGLB(
      `${CASTLE_ROOT}/${piece.file}`,
      piece.height * (0.92 + rnd() * 0.16),
      index % 2 === 0 ? 2.5 : 1.8,
      index % 2 === 0 ? 2.2 : 1.6,
      materials,
    );
    model.position.set(piece.x + (rnd() - 0.5) * 0.24, piece.roll === 0 ? 0 : 0.28, piece.z);
    model.rotation.set(0, rnd() * Math.PI, piece.roll);
    group.add(model);
  })).catch((error: unknown) => console.error('Unable to load rubble models', error));
}

function crystalMaterials(type: DepositType): {
  shades: THREE.MeshStandardMaterial[];
  base: THREE.MeshStandardMaterial;
} {
  const power = type === 'power';
  const colors = power ? [0xb9f3ff, 0x53c8f1, 0x1778ae] : [0xf0c3ff, 0xc275f0, 0x7133aa];
  return {
    shades: colors.map((color) => new THREE.MeshStandardMaterial({
      color,
      roughness: 0.3,
      metalness: 0.06,
      flatShading: true,
    })),
    base: new THREE.MeshStandardMaterial({ color: 0x514d45, roughness: 0.98, flatShading: true }),
  };
}

/** Immediate resource silhouette used until the imported crystal cluster is ready. */
export function makeProceduralDepositFallback(type: DepositType, seed: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'deposit-fallback';
  const materials = crystalMaterials(type);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1.75, 2.05, 0.5, 7), materials.base);
  base.position.y = 0.25;
  base.rotation.y = seed * 0.37;
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  const rnd = terrainRandom(seed ^ (type === 'power' ? 0x4f1bbcdc : 0x94d049bb));
  const placements = [
    { x: 0, z: 0, height: 2.9, width: 0.7 },
    { x: -0.75, z: 0.25, height: 2.05, width: 0.55 },
    { x: 0.7, z: 0.45, height: 1.75, width: 0.5 },
    { x: 0.25, z: -0.72, height: 1.55, width: 0.45 },
    { x: -0.55, z: -0.62, height: 1.35, width: 0.42 },
  ];
  for (let index = 0; index < placements.length; index++) {
    const placement = placements[index];
    const crystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(1, 0),
      materials.shades[index % materials.shades.length],
    );
    crystal.scale.set(placement.width, placement.height, placement.width);
    crystal.position.set(placement.x, 0.42 + placement.height, placement.z);
    crystal.rotation.set((rnd() - 0.5) * 0.18, rnd() * Math.PI, (rnd() - 0.5) * 0.18);
    crystal.castShadow = true;
    crystal.receiveShadow = true;
    group.add(crystal);
  }
  group.rotation.y = ((seed * 2.399963229728653) % (Math.PI * 2)) + (type === 'power' ? 0.18 : -0.18);
  return group;
}

/** Populates a deposit root with a naturally shaded CC0 crystal cluster; selection owns its ring. */
export function populateDepositModel(group: THREE.Group, type: DepositType, seed: number): void {
  const fallback = makeProceduralDepositFallback(type, seed);
  group.add(fallback);
  void replaceFallbackWhenLoaded(group, fallback, async () => {
    const object = (await sourceOBJ(CRYSTAL_MODEL)).clone(true);
    const materials = crystalMaterials(type);
    let crystalSlot = 0;
    styleMeshes(object, (original) => {
      if (original.name === 'Material.011') return materials.base;
      return materials.shades[crystalSlot++ % materials.shades.length];
    });
    fitModel(object, 3.7, 4.1, 4.1);
    object.position.y -= 0.12;
    const model = new THREE.Group();
    model.name = 'deposit-model';
    model.add(object);
    model.rotation.y = ((seed * 2.399963229728653) % (Math.PI * 2)) + (type === 'power' ? 0.18 : -0.18);
    return model;
  }).then((result) => {
    if (result.ok) return;
    reportAssetFailure(
      'crystal-deposit-model',
      `Unable to load crystal deposit model ${CRYSTAL_MODEL}; procedural crystal fallbacks retained.`,
      result.error,
    );
  });
}
